import React, { useState, useMemo, useEffect } from 'react'
import { Drawer, Checkbox, Button, Tag, Input } from 'antd'
import {
  DownloadOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import './style.scss'
import invoiceApi from 'api/invoiceApi'
import ToastHelper from 'general/helpers/ToastHelper'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
const CustomDrawer = ({
  drawerDowloadFie,
  setDrawerDowloadFie,
  title,
  width,
  files = [],
  onRefetchItem,
  clearDrawerDowloadFie,
}) => {
  const [selected, setSelected] = useState([])
  const [visibleCount, setVisibleCount] = useState(50)
  const [search, setSearch] = useState('')
  const [disableDownloadFile, setDisableDownloadFile] = useState(false)

  const handleClose = () => {
    setDrawerDowloadFie({ ...drawerDowloadFie, show: false })
  }

  const isSuccess = (item) => item.status === 'success'

  // =========================
  // TOGGLE SELECT (KEY + URL)
  // =========================
  const toggleSelect = (file, type) => {
    console.log('file', file)
    if (!isSuccess(file)) return

    const key = `${file.invoiceId}_${type}`
    const url = `${process.env.REACT_APP_BASE_URL}${file?.[type]?.fileURL}`
    const nameFile = url.includes('.pdf')
      ? file?.pdf?.nameFile
      : file?.xml?.nameFile

    setSelected((prev) => {
      const exists = prev.find((i) => i.key === key)

      if (exists) {
        return prev.filter((i) => i.key !== key)
      }

      return [...prev, { key, url, nameFile }]
    })
  }

  // =========================
  // SEARCH
  // =========================
  const filteredFiles = useMemo(() => {
    if (!search.trim()) return files

    const keyword = search.toLowerCase()

    return files.filter((item) => {
      const pdfName = item?.pdf?.nameFile?.toLowerCase() || ''
      const xmlName = item?.xml?.nameFile?.toLowerCase() || ''
      return pdfName.includes(keyword) || xmlName.includes(keyword)
    })
  }, [files, search])

  // =========================
  // PAGING
  // =========================
  const visibleFiles = useMemo(() => {
    return filteredFiles.slice(0, visibleCount)
  }, [filteredFiles, visibleCount])

  useEffect(() => {
    setVisibleCount(50)
  }, [search])

  // =========================
  // STATUS COUNT
  // =========================
  const countStatus = useMemo(() => {
    const result = {
      success: 0,
      error: 0,
      processing: 0,
      waiting: 0,
    }

    filteredFiles.forEach((item) => {
      switch (item.status) {
        case 'success':
          result.success++
          break
        case 'error':
          result.error++
          break
        case 'processing':
          result.processing++
          break
        default:
          result.waiting++
      }
    })

    return result
  }, [filteredFiles])

  // =========================
  // KEYS BUILD
  // =========================
  const successKeys = useMemo(() => {
    const arr = []
    filteredFiles.forEach((item) => {
      if (isSuccess(item)) {
        if (item.pdf) arr.push(`${item.invoiceId}_pdf`)
        if (item.xml) arr.push(`${item.invoiceId}_xml`)
      }
    })
    return arr
  }, [filteredFiles])

  const pdfKeys = useMemo(() => {
    return filteredFiles
      .filter((item) => isSuccess(item) && item.pdf)
      .map((item) => `${item.invoiceId}_pdf`)
  }, [filteredFiles])

  const xmlKeys = useMemo(() => {
    return filteredFiles
      .filter((item) => isSuccess(item) && item.xml)
      .map((item) => `${item.invoiceId}_xml`)
  }, [filteredFiles])

  // =========================
  // CHECK ALL
  // =========================
  const handleCheckAll = (e) => {
    if (e.target.checked) {
      const all = filteredFiles.flatMap((item) => {
        const arr = []
        if (item.pdf && isSuccess(item)) {
          arr.push({
            key: `${item.invoiceId}_pdf`,
            url: `${process.env.REACT_APP_BASE_URL}${item.pdf.fileURL}`,
            nameFile: item?.pdf?.nameFile,
          })
        }
        if (item.xml && isSuccess(item)) {
          arr.push({
            key: `${item.invoiceId}_xml`,
            url: `${process.env.REACT_APP_BASE_URL}${item.xml.fileURL}`,
            nameFile: item?.xml?.nameFile,
          })
        }
        return arr
      })

      setSelected(all)
    } else {
      setSelected([])
    }
  }

  // =========================
  // CHECK TYPE
  // =========================
  const handleCheckType = (type, checked) => {
    const keys = type === 'pdf' ? pdfKeys : xmlKeys

    const items = filteredFiles.flatMap((item) => {
      if (!isSuccess(item)) return []

      if (type === 'pdf' && item.pdf) {
        return [
          {
            key: `${item.invoiceId}_pdf`,
            url: `${process.env.REACT_APP_BASE_URL}${item.pdf.fileURL}`,
            nameFile: item?.pdf?.nameFile,
          },
        ]
      }

      if (type === 'xml' && item.xml) {
        return [
          {
            key: `${item.invoiceId}_xml`,
            url: `${process.env.REACT_APP_BASE_URL}${item.xml.fileURL}`,
            nameFile: item?.xml?.nameFile,
          },
        ]
      }

      return []
    })

    if (checked) {
      setSelected((prev) => {
        const map = new Map(prev.map((i) => [i.key, i]))
        items.forEach((i) => map.set(i.key, i))
        return Array.from(map.values())
      })
    } else {
      setSelected((prev) => prev.filter((i) => !keys.includes(i.key)))
    }
  }

  // =========================
  // STATUS UI
  // =========================
  const renderStatus = (status) => {
    switch (status) {
      case 'success':
        return (
          <Tag color="green" style={{ height: 22 }}>
            Thành công
          </Tag>
        )
      case 'error':
        return (
          <Tag color="red" style={{ height: 22 }}>
            Lỗi
          </Tag>
        )
      case 'processing':
        return (
          <Tag color="orange" style={{ height: 22 }}>
            Đang xử lý
          </Tag>
        )
      default:
        return <Tag style={{ height: 22 }}>Chờ xử lý</Tag>
    }
  }

  // =========================
  // RETRY
  // =========================
  const downloadFileRetry = async (invoiceIds) => {
    const res = await invoiceApi.downloadFileQueue({ invoiceIds })
    if (res == true) {
      ToastHelper.showSuccess('Đã làm mới file hóa đơn')
      return res
    } else {
      ToastHelper.showError('Lỗi tải file hóa đơn')
    }
  }

  // =========================
  // DOWNLOAD SINGLE
  // =========================
  const downloadFile = async (url, fileName) => {
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Download failed')

      const blob = await res.blob()
      const blobUrl = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName || 'download'
      document.body.appendChild(link)
      link.click()

      link.remove()
      window.URL.revokeObjectURL(blobUrl)
    } catch (err) {
      console.error(err)
    }
  }

  // =========================
  // DOWNLOAD SELECTED
  // =========================

  const handleDownloadSelected = async () => {
    setDisableDownloadFile(true)
    if (!selected.length) return

    try {
      const zip = new JSZip()
      console.log('selected', selected)
      await Promise.all(
        selected.map(async (item) => {
          const res = await fetch(item.url)
          if (!res.ok) return

          const blob = await res.blob()

          const fileName = item.url.split('/').pop()
          zip.file(item.nameFile || fileName, blob)
        }),
      )

      const zipBlob = await zip.generateAsync({ type: 'blob' })

      saveAs(zipBlob, `invoices_${Date.now()}.zip`)
      setDisableDownloadFile(false)
    } catch (err) {
      console.error(err)
      ToastHelper.showError('Lỗi tạo file zip')
      setDisableDownloadFile(false)
    }
  }

  return (
    <Drawer
      title={title}
      placement="right"
      onClose={handleClose}
      open={drawerDowloadFie?.show}
      width={width}
    >
      <div className="dd-wrapper">
        {/* SEARCH */}
        <Input
          placeholder="Tìm file..."
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* HEADER */}
        <div className="dd-header">
          <div className="dd-header-left">
            <Checkbox
              onChange={handleCheckAll}
              checked={
                selected.length > 0 && selected.length === successKeys.length
              }
            >
              Tất cả
            </Checkbox>

            <Checkbox
              onChange={(e) => handleCheckType('pdf', e.target.checked)}
              checked={pdfKeys.every((k) => selected.some((s) => s.key === k))}
            >
              PDF
            </Checkbox>

            <Checkbox
              onChange={(e) => handleCheckType('xml', e.target.checked)}
              checked={xmlKeys.every((k) => selected.some((s) => s.key === k))}
            >
              XML
            </Checkbox>
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              size="small"
              onClick={() => {
                clearDrawerDowloadFie()
                setSelected([])
              }}
            >
              Xóa toàn bộ
            </Button>

            <Button
              size="small"
              type="primary"
              disabled={!selected.length || disableDownloadFile}
              onClick={handleDownloadSelected}
            >
              {disableDownloadFile
                ? `Đang tải ${selected.length || ''} file`
                : `Tải ${selected.length} file`}
            </Button>
          </div>
        </div>

        {/* STATUS */}
        <div className="dd-status">
          <Tag color="blue">Tổng ({filteredFiles.length})</Tag>
          <Tag color="green">Thành công ({countStatus.success})</Tag>
          <Tag color="red">Lỗi ({countStatus.error})</Tag>
          <Tag color="orange">Đang xử lý ({countStatus.processing})</Tag>
          <Tag>Chờ ({countStatus.waiting})</Tag>
        </div>

        {/* LIST */}
        {visibleFiles.map((item) => (
          <div
            key={item.invoiceId}
            className={`dd-item ${!isSuccess(item) ? 'disabled' : ''}`}
          >
            <div className="dd-item-header">
              <span>
                {item.serial} - {item.no}
              </span>

              <div style={{ display: 'flex', gap: 8 }}>
                {renderStatus(item.status)}

                <Button
                  icon={<UndoOutlined />}
                  onClick={() => downloadFileRetry([item.invoiceId])}
                />
              </div>
            </div>

            <div className="dd-files">
              {/* PDF */}
              <div
                className="dd-file pdf"
                onClick={() =>
                  downloadFile(
                    `${process.env.REACT_APP_BASE_URL}${item?.pdf?.fileURL}`,
                    item?.pdf?.nameFile,
                  )
                }
              >
                <Checkbox
                  disabled={!isSuccess(item)}
                  checked={selected.some(
                    (i) => i.key === `${item.invoiceId}_pdf`,
                  )}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(item, 'pdf')}
                />
                <FilePdfOutlined className="icon" />
                <span>{item?.pdf?.nameFile}</span>
              </div>

              {/* XML */}
              <div
                className="dd-file xml"
                onClick={() =>
                  downloadFile(
                    `${process.env.REACT_APP_BASE_URL}${item?.xml?.fileURL}`,
                    item?.xml?.nameFile,
                  )
                }
              >
                <Checkbox
                  disabled={!isSuccess(item)}
                  checked={selected.some(
                    (i) => i.key === `${item.invoiceId}_xml`,
                  )}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(item, 'xml')}
                />
                <FileTextOutlined className="icon" />
                <span>{item?.xml?.nameFile}</span>
              </div>
            </div>
          </div>
        ))}

        {/* LOAD MORE */}
        {visibleCount < filteredFiles.length && (
          <Button
            block
            size="small"
            onClick={() => setVisibleCount((prev) => prev + 50)}
          >
            Xem thêm ({filteredFiles.length - visibleCount})
          </Button>
        )}

        {/* FOOTER */}
        <Button
          type="primary"
          block
          disabled={!selected.length || disableDownloadFile}
          onClick={handleDownloadSelected}
        >
          {disableDownloadFile
            ? `Đang tải ${selected.length || ''} file`
            : `Tải ${selected.length} file`}
        </Button>
      </div>
    </Drawer>
  )
}

export default CustomDrawer
